<template>
  <section class="section admin-ai-visibility-trackers">
    <div class="container">
      <div class="columns">
        <div class="column">
          <div class="columns is-desktop">
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model.number="perPage" @change="onPerPageChange">
                      <option :value="10">10 per page</option>
                      <option :value="15">15 per page</option>
                      <option :value="25">25 per page</option>
                      <option :value="50">50 per page</option>
                      <option :value="100">100 per page</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <div class="control">
                  <input
                    v-model="searchText"
                    type="text"
                    class="input"
                    placeholder="Search brand, domain, owner, site, or ID"
                    @keyup.enter="onFilter"
                  >
                </div>
              </div>
            </div>
            <div class="column is-2">
              <button class="button is-info is-fullwidth" @click="onFilter">Search</button>
            </div>
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model="exportReportType">
                      <option value="trackers">Tracker Summary</option>
                      <option value="runs">Run Details</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column is-2">
              <button
                class="button is-link is-fullwidth"
                :class="{ 'is-loading': isExportingPage }"
                :disabled="isExportingPage || isExportingAll"
                @click="exportPage"
              >
                Export Page
              </button>
            </div>
            <div class="column is-2">
              <button
                class="button is-link is-fullwidth"
                :class="{ 'is-loading': isExportingAll }"
                :disabled="isExportingPage || isExportingAll"
                @click="exportAll"
              >
                Export All
              </button>
            </div>
          </div>

          <b-table
            ref="trackersTable"
            :data="records"
            :loading="isLoading"
            paginated
            backend-pagination
            :total="totalRows"
            :per-page="perPage"
            :current-page="currentPage"
            @page-change="onPageChange"
            backend-sorting
            :default-sort-direction="defaultSortOrder"
            :default-sort="[sortField, sortOrder]"
            @sort="onSort"
            detailed
            detail-key="id"
            @click="toggleRowDetails"
          >
            <template slot-scope="props">
              <b-table-column field="id" label="Report ID" sortable numeric>{{ props.row.id }}</b-table-column>
              <b-table-column field="brandName" label="Brand" sortable>{{ props.row.brandName }}</b-table-column>
              <b-table-column field="domain" label="Domain" sortable>{{ props.row.domain || '-' }}</b-table-column>
              <b-table-column field="ownerEmail" label="Owner" sortable>{{ props.row.ownerEmail }}</b-table-column>
              <b-table-column field="categories" label="Categories">{{ formatCategories(props.row.categories) }}</b-table-column>
              <b-table-column field="market" label="Location/Market">{{ formatLocationMarket(props.row) }}</b-table-column>
              <b-table-column field="status" label="Status" sortable>{{ props.row.status }}</b-table-column>
              <b-table-column field="refreshCadence" label="Cadence" sortable>{{ props.row.refreshCadence }}</b-table-column>
              <b-table-column field="totalRunCount" label="Runs" sortable numeric>{{ props.row.totalRunCount }}</b-table-column>
              <b-table-column field="totalCredits" label="Total Credits" sortable numeric>{{ props.row.totalCredits }}</b-table-column>
              <b-table-column field="lastRunStatus" label="Last Run" sortable>{{ props.row.lastRunStatus || '-' }}</b-table-column>
              <b-table-column field="lastRunAt" label="Last Run At" sortable>{{ formatDate(props.row.lastRunAt) }}</b-table-column>
              <b-table-column field="nextRunAt" label="Next Run At" sortable>{{ formatDate(props.row.nextRunAt) }}</b-table-column>
              <b-table-column field="createdAt" label="Created" sortable>{{ formatDate(props.row.createdAt) }}</b-table-column>
            </template>
            <template slot="detail" slot-scope="props">
              <div class="admin-ai-visibility-trackers__detail" @click.stop>
                <ai-visibility-detail
                  embedded
                  :embedded-site-id="props.row.siteId"
                  :embedded-tracker-id="props.row.id"
                />
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { toLocalDateString } from '@/utils'
import AiVisibilityDetail from '@/components/AiVisibility/detail/Index.vue'
import {
  exportAdminAiVisibilityTrackerRunsAll,
  exportAdminAiVisibilityTrackerRunsPage,
  exportAdminAiVisibilityTrackersAll,
  exportAdminAiVisibilityTrackersPage,
  getAdminAiVisibilityTrackers,
} from '@/components/AiVisibility/api'

export default {
  name: 'AdminAiVisibilityTrackers',
  components: {
    AiVisibilityDetail,
  },
  data () {
    return {
      records: [],
      totalRows: 0,
      isLoading: false,
      sortField: 'id',
      sortOrder: 'desc',
      defaultSortOrder: 'asc',
      currentPage: 1,
      perPage: 15,
      searchText: '',
      isExportingPage: false,
      isExportingAll: false,
      exportReportType: 'trackers',
    }
  },
  methods: {
    formatDate (dateStr) {
      return dateStr ? toLocalDateString(dateStr) : '-'
    },
    formatCategories (categories) {
      if (!Array.isArray(categories) || !categories.length) {
        return '-'
      }
      return categories.join(', ')
    },
    formatLocationMarket (row) {
      return [
        row.market,
        row.countryIsoCode,
        row.language,
      ].filter(Boolean).join(' / ') || '-'
    },
    getRequestParams () {
      return {
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        page: this.currentPage,
        perPage: this.perPage,
        filterText: this.searchText,
      }
    },
    getExportFileName (scope) {
      const now = new Date()
      const date = `${now.getUTCFullYear()}_${now.getUTCMonth() + 1}_${now.getUTCDate()}`
      const reportName = this.exportReportType === 'runs'
        ? 'AiVisibilityTrackerRuns'
        : 'AiVisibilityTrackers'
      return scope === 'page'
        ? `${reportName}_${date}_Page${this.currentPage}.csv`
        : `${reportName}_${date}_All.csv`
    },
    getExportRequest (scope) {
      if (this.exportReportType === 'runs') {
        return scope === 'page'
          ? exportAdminAiVisibilityTrackerRunsPage
          : exportAdminAiVisibilityTrackerRunsAll
      }
      return scope === 'page'
        ? exportAdminAiVisibilityTrackersPage
        : exportAdminAiVisibilityTrackersAll
    },
    downloadCsvBlob (blob, fileName) {
      const downloadLink = document.createElement('a')
      const url = window.URL.createObjectURL(blob)
      downloadLink.download = fileName
      downloadLink.href = url
      downloadLink.style.display = 'none'
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      window.URL.revokeObjectURL(url)
    },
    exportPage () {
      this.isExportingPage = true
      this.$store.commit('showLoading')
      return this.getExportRequest('page')(this.getRequestParams())
        .then(({ data }) => {
          this.downloadCsvBlob(data, this.getExportFileName('page'))
        })
        .finally(() => {
          this.isExportingPage = false
          this.$store.commit('hideLoading')
        })
    },
    exportAll () {
      this.isExportingAll = true
      this.$store.commit('showLoading')
      return this.getExportRequest('all')(this.getRequestParams())
        .then(({ data }) => {
          this.downloadCsvBlob(data, this.getExportFileName('all'))
        })
        .finally(() => {
          this.isExportingAll = false
          this.$store.commit('hideLoading')
        })
    },
    onPerPageChange () {
      this.currentPage = 1
      this.loadData()
    },
    onPageChange (page) {
      this.currentPage = page
      this.loadData()
    },
    onSort (field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadData()
    },
    onFilter () {
      this.currentPage = 1
      this.loadData()
    },
    toggleRowDetails (row) {
      if (this.$refs.trackersTable && row) {
        this.$refs.trackersTable.toggleDetails(row)
      }
    },
    loadData () {
      this.isLoading = true
      this.$store.commit('showLoading')
      return getAdminAiVisibilityTrackers(this.getRequestParams())
        .then(({ data }) => {
          this.totalRows = data.total || 0
          this.records = data.records || []
        })
        .finally(() => {
          this.isLoading = false
          this.$store.commit('hideLoading')
        })
    },
  },
  beforeMount () {
    this.loadData()
  },
}
</script>

<style lang="scss" scoped>
.admin-ai-visibility-trackers__detail {
  background: var(--v-gray7Color-base);
  border: 1px solid var(--v-gray13Color-base);
  border-radius: 6px;
  overflow: hidden;
}

.admin-ai-visibility-trackers ::v-deep .table tr {
  cursor: pointer;
}
</style>
